import React, { useState } from 'react';
import { useTranslate, useNavigation, useNotification } from '@refinedev/core';
import { 
  Button, 
  Card, 
  Tag, 
  Tabs,
} from '@scalingworks/react-admin-ui';
import { 
  FaEdit, 
  FaCopy, 
  FaPlay, 
  FaPause, 
  FaStamp, 
  FaGift,
  FaCrown,
  FaUsers,
  FaChartLine,
  FaCode,
  FaCheck,
  FaTimes,
} from 'react-icons/fa';
import { 
  MdVisibility, 
  MdInfo,
  MdSettings,
  MdCardGiftcard,
  MdAccessTime,
} from 'react-icons/md';
import { generateMockStampCardData, testScenarios } from '../config/test-data';
import { ShowPageWrapper, Loading, SomethingWentWrong } from '~/components';
import { resourceNames } from '~/resources/resource-names';
import { StampTemplate, CheckerConfigType, StampingRuleType, StampMilestone } from '~/api';
import { capitalize, startCase } from 'lodash';
import { formatCentsToRinggit } from '~/utils/currency';
import StampCardIframe from '~/components/StampCard/StampCardIframe';

interface StampTemplateShowPageProps {
  data: StampTemplate;
  loading?: boolean;
  resourceId: string;
}

export const StampTemplateShowPage: React.FC<StampTemplateShowPageProps> = ({
  data: stampTemplateData,
  loading = false,
  resourceId,
}) => {
  const t = useTranslate();
  const { edit } = useNavigation();
  const [selectedPreviewScenario, setSelectedPreviewScenario] = useState('default');
  const [showMilestones, setShowMilestones] = useState(true);
  
  if (loading) return <Loading />;
  if (!stampTemplateData) return <SomethingWentWrong />;

  // Generate mock data for preview
  const selectedScenario = testScenarios.find(s => s.key === selectedPreviewScenario) || testScenarios[0];
  const mockStampCardData = generateMockStampCardData({
    totalStamps: stampTemplateData.totalStampsRequired || 10,
    earnedStamps: selectedScenario.earnedStamps,
    iconUrl: stampTemplateData.iconUrl || '',
    iconFilledUrl: stampTemplateData.iconFilledUrl || '',
    iconWithRewardUrl: stampTemplateData.iconWithRewardUrl || '',
    iconWithRewardFilledUrl: stampTemplateData.iconWithRewardFilledUrl || '',
    milestonePositions: showMilestones ? [3, 6, 10] : [],
    scenario: selectedPreviewScenario,
  });

  const handleEditTemplate = () => {
    edit(resourceNames.stampTemplate, resourceId);
  };

  const getStatusTag = (isActive: boolean) => (
    <Tag 
      className={isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}
    >
      <div className="flex items-center gap-1">
        {isActive ? <FaCheck size={12} /> : <FaTimes size={12} />}
        {isActive ? t('common.active') : t('common.inactive')}
      </div>
    </Tag>
  );

  const renderEligibilityCheckers = () => {
    const checkers = stampTemplateData.eligibilityCheckers || [];
    if (checkers.length === 0) {
      return (
        <div className="text-gray-500 text-sm italic">
          {t('stampTemplate.eligibility.noRequirements')}
        </div>
      );
    }

    return (
      <div className="space-y-3">
        {checkers.map((checker: CheckerConfigType, index: number) => {
          // Format checker description based on type and config
          const formatCheckerDescription = () => {
            if (checker.description) return checker.description;
            
            // Generate human-readable descriptions based on type
            switch (checker.type) {
              case 'minimum-order-amount':
                return `Minimum order amount: RM${checker.config?.amount || 0}`;
              case 'product-category':
                return `Product category: ${checker.config?.category || 'Any'}`;
              case 'customer-tier':
                return `Customer tier: ${checker.config?.tier || 'Any'}`;
              case 'first-time-customer':
                return 'First-time customers only';
              default:
                return checker.type?.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Eligibility Rule';
            }
          };

          const hasNoType = !checker.type;
          const description = formatCheckerDescription();

          return (
            <Card key={index} className="p-4 bg-blue-50 border-blue-200 hover:shadow-sm transition-shadow">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <FaCode className="text-blue-600" size={16} />
                </div>
                <div className="flex-1">
                  <h5 className="font-semibold text-blue-900 text-base">
                    {description}
                  </h5>
                  {hasNoType && (
                    <p className="text-sm text-blue-700 mt-1">
                      All customers are eligible for this stamp template
                    </p>
                  )}
                  {checker.type && !checker.description && (
                    <p className="text-sm text-blue-700 mt-1">
                      Type: <code className="bg-blue-200 px-1 rounded text-xs">{checker.type}</code>
                    </p>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    );
  };

  const renderStampingRules = () => {
    const rules = stampTemplateData.stampingRules || [];
    if (rules.length === 0) {
      return (
        <div className="text-gray-500 text-sm italic">
          {t('stampTemplate.stamping.noRules')}
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {rules.map((rule: StampingRuleType, index: number) => {
          // Format action description
          const formatActionDescription = () => {
            const actionType = rule.action?.type || 'add-stamps';
            const config = rule.action?.config || {};
            
            switch (actionType) {
              case 'add-stamps':
                return `Add ${config.count || 1} stamp${(config.count || 1) > 1 ? 's' : ''}`;
              case 'add-bonus-stamps':
                return `Add ${config.count || 1} bonus stamp${(config.count || 1) > 1 ? 's' : ''}`;
              case 'multiply-stamps':
                return `Multiply stamps by ${config.multiplier || 2}x`;
              default:
                return actionType.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
            }
          };

          // Format condition description
          const formatConditionDescription = (checker: CheckerConfigType) => {
            if (checker.description) return checker.description;
            
            switch (checker.type) {
              case 'minimum-order-amount':
                return `For orders at least RM${checker.config?.amount || 0}`;
              case 'product-category':
                return `For ${checker.config?.category || 'specific'} category`;
              case 'time-range':
                return `During ${checker.config?.start || 'start'} - ${checker.config?.end || 'end'}`;
              case 'day-of-week':
                return `On ${checker.config?.days?.join(', ') || 'specific days'}`;
              case 'first-purchase':
                return 'First purchase only';
              default:
                return checker.type?.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Condition';
            }
          };

          return (
            <Card key={index} className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 border-green-200 hover:shadow-md transition-shadow">
              <div className="flex items-start gap-4">
                <div className="p-2 bg-green-100 rounded-lg">
                  <FaChartLine className="text-green-600" size={18} />
                </div>
                
                <div className="flex-1">
                  {/* Header with Priority inline */}
                  <div className="flex items-center gap-3 mb-3 flex-wrap">
                    <h5 className="text-lg font-bold text-gray-900">
                      Stamping Rule #{index + 1}
                    </h5>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-600 text-white">
                      Priority {rule.priority || 1}
                    </span>
                  </div>
                  
                  {/* Action Section */}
                  <div className="mb-4">
                    <div className="text-sm font-semibold text-gray-700 mb-2">Action</div>
                    <div className="bg-green-100 border border-green-200 rounded-lg p-3">
                      <div className="text-base font-semibold text-green-900">
                        {formatActionDescription()}
                      </div>
                    </div>
                  </div>

                  {/* Conditions Section */}
                  {rule.checkers && rule.checkers.length > 0 && (
                    <div>
                      <div className="text-sm font-semibold text-gray-700 mb-2">
                        Conditions ({rule.checkers.length})
                      </div>
                      <div className="space-y-2">
                        {rule.checkers.map((checker: CheckerConfigType, cIndex: number) => (
                          <div key={cIndex} className="bg-green-100 border border-green-200 rounded-lg p-3">
                            <div className="text-sm font-semibold text-green-900">
                              {formatConditionDescription(checker)}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* No conditions */}
                  {(!rule.checkers || rule.checkers.length === 0) && (
                    <div className="text-sm text-gray-600 italic">
                      No conditions - applies to all eligible transactions
                    </div>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    );
  };

  const renderMilestones = () => {
    const milestones = stampTemplateData.milestones || [];
    console.log({milestones})
    if (milestones.length === 0) {
      return (
        <div className="text-gray-500 text-sm italic">
          {t('stampTemplate.milestones.none')}
        </div>
      );
    }

    return (
      <div className="space-y-3">
        {milestones.map((milestone: StampMilestone, index: number) => {
          const stampsRequired = milestone.milestoneStamps || 0;
          const rewardType = milestone.rewardType || 'NONE';
          const rewardValue = milestone.rewardValue || '0';
          
          // Format reward value based on type
          const formatRewardValue = () => {
            if (rewardType === 'POINTS') {
              return `${rewardValue} points`;
            } else if (rewardType === 'REWARD') {
              // Show a truncated version of the reward ID or a friendly name
              return milestone.name || 'Reward Item';
            } else if (rewardType === 'STORE_CREDIT') {
              // Convert cents to ringgit with proper formatting
              return `RM${formatCentsToRinggit(Number(rewardValue))}`;
            }
            return rewardValue;
          };

          return (
            <Card key={index} className="p-4 bg-gradient-to-r from-yellow-50 to-amber-50 border-yellow-200 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  {/* Icon Section */}
                  <div className="p-3 bg-gradient-to-br from-yellow-100 to-amber-100 rounded-xl shadow-sm">
                    {milestone.iconUrl ? (
                      <img 
                        src={milestone.iconUrl} 
                        alt={milestone.name || `Milestone ${index + 1}`}
                        className="w-8 h-8 object-cover rounded"
                      />
                    ) : (
                      <FaCrown className="text-yellow-600" size={24} />
                    )}
                  </div>
                  
                  {/* Content Section */}
                  <div className="flex-1">
                    <div className="mb-2">
                      <h5 className="font-bold text-lg text-gray-900">
                        {milestone.name || `Milestone ${index + 1}`}
                      </h5>
                      {milestone.description && (
                        <p className="text-sm text-gray-600 mt-1">
                          {milestone.description}
                        </p>
                      )}
                    </div>

                    {/* Reward Details */}
                    <div className="flex flex-wrap gap-4 text-sm">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-gray-700">Reward Type:</span>
                        <Tag className={`
                          ${rewardType === 'POINTS' ? 'bg-blue-100 text-blue-800' : ''}
                          ${rewardType === 'REWARD' ? 'bg-purple-100 text-purple-800' : ''}
                          ${rewardType === 'STORE_CREDIT' ? 'bg-green-100 text-green-800' : ''}
                          ${rewardType === 'NONE' ? 'bg-gray-100 text-gray-800' : ''}
                        `}>
                          {capitalize(startCase(rewardType))}
                        </Tag>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-gray-700">Value:</span>
                        <span className="font-medium text-gray-900">{formatRewardValue()}</span>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-gray-700">Auto Claim:</span>
                        <Tag className={milestone.autoClaimable ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                          {milestone.autoClaimable ? t('common.yes') : t('common.no')}
                        </Tag>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Stamps Required - More prominent */}
                <div className="flex flex-col items-center justify-center min-w-[80px]">
                  <div className="text-3xl font-bold text-yellow-600">
                    {stampsRequired}
                  </div>
                  <div className="text-xs text-gray-600 uppercase tracking-wider">
                    stamps
                  </div>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    );
  };

  return (
    <ShowPageWrapper
      resourceName={resourceNames.stampTemplate}
      title={stampTemplateData.name || 'Untitled Template'}
      loading={loading}
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Header Actions */}
          <Card className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-indigo-100 rounded-lg">
                  <FaStamp className="text-indigo-600" size={20} />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-900">
                    {stampTemplateData.name}
                  </h1>
                  <p className="text-sm text-gray-600">
                    {stampTemplateData.description || 'No description provided'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {getStatusTag(stampTemplateData.isActive || false)}
              </div>
            </div>

            <div className="flex items-center gap-2 mt-4">
              <Button
                variant="solid"
                onClick={handleEditTemplate}
                className="flex items-center gap-2"
              >
                <FaEdit size={14} />
                {t('stampTemplate.actions.edit')}
              </Button>
             
            </div>
          </Card>

          {/* Template Configuration */}
          <Card className="p-4">
            <Tabs.Root defaultValue="overview">
              <Tabs.List>
                <Tabs.Trigger value="overview">
                  <MdInfo className="mr-2" />
                  {t('stampTemplate.tabs.overview')}
                </Tabs.Trigger>
                <Tabs.Trigger value="earning-rules">
                  <FaCode className="mr-2" />
                  {t('stampTemplate.tabs.earningRules')}
                </Tabs.Trigger>
                <Tabs.Trigger value="milestones">
                  <FaCrown className="mr-2" />
                  {t('stampTemplate.tabs.milestones')}
                </Tabs.Trigger>
                <Tabs.Trigger value="settings">
                  <MdSettings className="mr-2" />
                  {t('stampTemplate.tabs.settings')}
                </Tabs.Trigger>
              </Tabs.List>

              <Tabs.Content value="overview" className="mt-4">
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-600">
                        {t('stampTemplate.fields.templateName')}
                      </label>
                      <div className="text-base font-medium text-gray-900 mt-1">
                        {stampTemplateData.name}
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-600">
                        {t('stampTemplate.fields.description')}
                      </label>
                      <div className="text-base font-medium text-gray-900 mt-1">
                        {stampTemplateData.description || t('common.notConfigured')}
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-600">
                        {t('stampTemplate.fields.requiredStamps')}
                      </label>
                      <div className="text-base font-medium text-gray-900 mt-1">
                        {stampTemplateData.totalStampsRequired}
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-600">
                        {t('common.status')}
                      </label>
                      <div className="mt-1">
                        {getStatusTag(stampTemplateData.isActive || false)}
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-600">
                        {t('stampTemplate.fields.autoActivate')}
                      </label>
                      <div className="text-base font-medium text-gray-900 mt-1">
                        {stampTemplateData.autoActivate ? t('common.yes') : t('common.no')}
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-600">
                        {t('stampTemplate.fields.allowDuplicates')}
                      </label>
                      <div className="text-base font-medium text-gray-900 mt-1">
                        {stampTemplateData.allowDuplicates ? t('common.yes') : t('common.no')}
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-600">
                        {t('stampTemplate.fields.maxCardsPerCustomer')}
                      </label>
                      <div className="text-base font-medium text-gray-900 mt-1">
                        {stampTemplateData.maxCardsPerCustomer || 1}
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-600">
                        {t('stampTemplate.fields.autoCreateOnOverflow')}
                      </label>
                      <div className="text-base font-medium text-gray-900 mt-1">
                        {stampTemplateData.autoCreateOnOverflow ? t('common.yes') : t('common.no')}
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-600">
                        {t('stampTemplate.fields.displayOrder')}
                      </label>
                      <div className="text-base font-medium text-gray-900 mt-1">
                        {stampTemplateData.displayOrder || t('common.notSet')}
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-600">
                        {t('stampTemplate.fields.validityPeriod')}
                      </label>
                      <div className="text-base font-medium text-gray-900 mt-1">
                        {stampTemplateData.validityDays 
                          ? `${stampTemplateData.validityDays} ${t('dateTimeUnits.days')}`
                          : t('stampTemplate.fields.neverExpires')}
                      </div>
                    </div>
                  </div>
                  
                  {/* Terms and Conditions */}
                  {stampTemplateData.termsAndConditions && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <label className="text-sm font-medium text-gray-600 block mb-2">
                        {t('stampTemplate.fields.termsAndConditions')}
                      </label>
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <pre className="text-sm text-gray-800 whitespace-pre-wrap font-sans">
                          {stampTemplateData.termsAndConditions}
                        </pre>
                      </div>
                    </div>
                  )}
                </div>
              </Tabs.Content>

              <Tabs.Content value="earning-rules" className="mt-4 space-y-4">
                <div>
                  <h4 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <FaUsers className="text-blue-600" />
                    {t('stampTemplate.eligibility.title')}
                  </h4>
                  {renderEligibilityCheckers()}
                </div>

                <div>
                  <h4 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <FaChartLine className="text-green-600" />
                    {t('stampTemplate.stamping.title')}
                  </h4>
                  {renderStampingRules()}
                </div>
              </Tabs.Content>

              <Tabs.Content value="milestones" className="mt-4">
                <div className="mb-4">
                  <h4 className="text-lg font-semibold text-gray-800 mb-2 flex items-center gap-2">
                    <FaGift className="text-yellow-600" />
                    {t('stampTemplate.milestones.title')}
                  </h4>
                  <p className="text-sm text-gray-600">
                    {t('stampTemplate.milestones.description')}
                  </p>
                </div>
                {renderMilestones()}
              </Tabs.Content>

              <Tabs.Content value="settings" className="mt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Card className="p-4 bg-gray-50">
                    <h5 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      <MdCardGiftcard className="text-purple-600" />
                      {t('stampTemplate.cardConfig.title')}
                    </h5>
                    <div className="space-y-3">
                      <div>
                        <label className="text-sm font-medium text-gray-600">
                          {t('stampTemplate.cardConfig.cardImage')}
                        </label>
                        <div className="mt-1">
                          {stampTemplateData.cardImageUrl ? (
                            <img 
                              src={stampTemplateData.cardImageUrl} 
                              alt={t('stampTemplate.cardConfig.cardBackgroundAlt')}
                              className="max-w-[100px] rounded"
                            />
                          ) : (
                            <span className="text-gray-500">{t('common.default')}</span>
                          )}
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">
                          {t('stampTemplate.cardConfig.stampIcon')}
                        </label>
                        <div className="mt-1">
                          {stampTemplateData.iconUrl ? (
                            <img 
                              src={stampTemplateData.iconUrl} 
                              alt={t('stampTemplate.cardConfig.stampIconAlt')}
                              className="w-6 h-6"
                            />
                          ) : (
                            <span className="text-gray-500">{t('common.default')}</span>
                          )}
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">
                          {t('stampTemplate.cardConfig.filledIcon')}
                        </label>
                        <div className="mt-1">
                          {stampTemplateData.iconFilledUrl ? (
                            <img 
                              src={stampTemplateData.iconFilledUrl} 
                              alt={t('stampTemplate.cardConfig.filledIconAlt')}
                              className="w-6 h-6"
                            />
                          ) : (
                            <span className="text-gray-500">{t('common.default')}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </Card>

                  <Card className="p-4 bg-gray-50">
                    <h5 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      <MdAccessTime className="text-orange-600" />
                      {t('stampTemplate.validityLimits.title')}
                    </h5>
                    <div className="space-y-3">
                      <div>
                        <label className="text-sm font-medium text-gray-600">
                          {t('stampTemplate.fields.validityPeriod')}
                        </label>
                        <div className="text-base font-medium text-gray-900 mt-1">
                          {stampTemplateData.validityDays 
                            ? `${stampTemplateData.validityDays} ${t('common.unit.days')}`
                            : t('stampTemplate.fields.neverExpires')}
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">
                          {t('stampTemplate.fields.maxCardsPerCustomer')}
                        </label>
                        <div className="text-base font-medium text-gray-900 mt-1">
                          {stampTemplateData.maxCardsPerCustomer || 1}
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">
                          {t('stampTemplate.fields.autoCreateOnOverflow')}
                        </label>
                        <div className="text-base font-medium text-gray-900 mt-1">
                          {stampTemplateData.autoCreateOnOverflow ? t('common.yes') : t('common.no')}
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">
                          {t('stampTemplate.fields.displayOrder')}
                        </label>
                        <div className="text-base font-medium text-gray-900 mt-1">
                          {stampTemplateData.displayOrder || t('common.notSet')}
                        </div>
                      </div>
                    </div>
                  </Card>
                </div>
              </Tabs.Content>
            </Tabs.Root>
          </Card>
        </div>

        {/* Preview Sidebar */}
        <div className="space-y-4">
          {/* Preview Controls */}
          <Card className="p-4">
            <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <MdVisibility className="text-indigo-600" />
              {t('stampTemplate.preview.controls')}
            </h4>
            
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('stampTemplate.preview.testScenario')}
                </label>
                <select
                  value={selectedPreviewScenario}
                  onChange={(e) => setSelectedPreviewScenario(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  {testScenarios.map((scenario) => (
                    <option key={scenario.key} value={scenario.key}>
                      {scenario.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {selectedScenario.description}
                </p>
              </div>

              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">
                  {t('stampTemplate.preview.showMilestones')}
                </label>
                <button
                  onClick={() => setShowMilestones(!showMilestones)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    showMilestones ? 'bg-indigo-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      showMilestones ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          </Card>

          {/* StampCard Preview */}
          <StampCardIframe
            data={mockStampCardData}
            theme={stampTemplateData?.metadata?.theme as any}
          />

          {/* Preview Stats */}
          <Card className="p-4 bg-blue-50 border-blue-200">
            <h5 className="font-semibold text-blue-800 mb-2">{t('stampTemplate.preview.information')}</h5>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-blue-700">{t('stampTemplate.preview.scenario')}</span>
                <span className="font-medium text-blue-800">{selectedScenario.label}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-blue-700">{t('stampTemplate.preview.progress')}</span>
                <span className="font-medium text-blue-800">
                  {selectedScenario.earnedStamps}/{stampTemplateData.totalStampsRequired || 10}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-blue-700">{t('stampTemplate.preview.milestones')}</span>
                <span className="font-medium text-blue-800">
                  {showMilestones ? t('common.visible') : t('common.hidden')}
                </span>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </ShowPageWrapper>
  );
};